import { type FC } from 'react'
import { Badge, CloseButton, Form } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { type ReminderSettings } from '../../../../models/Scheduling'
import { SettingsCard } from './SettingsSection'
import styles from './SchedulingSettings.module.scss'
import {
  createRowDraft,
  formatReminderOffset,
  MAX_REMINDER_ENTRIES,
  nextReminderOffset,
  settingsChanged,
  type ReminderRowDraft,
  type ReminderSettingsDraft,
} from './reminderDrafts'

const NEW_ROW_CAVEAT = "New steps only apply to send times that haven't passed yet."

export interface ReminderSettingsCopy {
  title: string
  heading: string
  description: string
  banner?: string
  rowLabel: string
  trailingCopy: string
  addLabel: string
  removeLabel: string
  removeLastTooltip: string
}

export interface ReminderSettingsCardProps {
  copy: ReminderSettingsCopy
  settings: ReminderSettings
  draft: ReminderSettingsDraft | null
  readOnly: boolean
  editDisabled: boolean
  editDisabledTooltip: string
  isSaving: boolean
  error: string | null
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  onDraftChange: (draft: ReminderSettingsDraft) => void
  onShowEmailTemplates: () => void
}

interface ReadOnlyRowsProps {
  copy: ReminderSettingsCopy
  settings: ReminderSettings
}

const ReadOnlyRows: FC<ReadOnlyRowsProps> = ({ copy, settings }) => (
  <div className={classNames(styles.reminderTimeline, 'd-flex flex-column gap-2')}>
    {settings.entries.map((entry) => (
      <div key={entry.id ?? entry.offsetMinutes} className='d-flex align-items-center gap-2'>
        <span className={styles.reminderTimelineDot} />
        <div className='flex-grow-1 zocks-bg-color-gray-100 rounded-2 p-3'>
          {copy.rowLabel} <span className='bold'>{formatReminderOffset(entry.offsetMinutes)}</span> {copy.trailingCopy}
        </div>
      </div>
    ))}
  </div>
)

const rowPillLabel = (row: ReminderRowDraft, settings: ReminderSettings): string | null => {
  if (row.id == null) {
    return 'New'
  }
  const savedEntry = settings.entries.find((entry) => entry.id === row.id)
  return savedEntry != null && row.offsetMinutes !== savedEntry.offsetMinutes ? 'Changed' : null
}

interface EditableRowsProps {
  copy: ReminderSettingsCopy
  settings: ReminderSettings
  draft: ReminderSettingsDraft
  onDraftChange: (draft: ReminderSettingsDraft) => void
}

const EditableRows: FC<EditableRowsProps> = ({ copy, settings, draft, onDraftChange }) => {
  const usedOffsets = draft.rows.map((row) => row.offsetMinutes)
  const removeDisabled = draft.rows.length === 1
  const updateRow = (key: string, offsetMinutes: number) =>
    onDraftChange({ ...draft, rows: draft.rows.map((row) => (row.key === key ? { ...row, offsetMinutes } : row)) })
  const removeRow = (key: string) => onDraftChange({ ...draft, rows: draft.rows.filter((row) => row.key !== key) })
  const addRow = () => onDraftChange({ ...draft, rows: [...draft.rows, createRowDraft(nextReminderOffset(settings.allowedOffsets, draft.rows))] })
  return (
    <div className='d-flex flex-column gap-2'>
      <div className={classNames(styles.reminderTimeline, 'd-flex flex-column gap-2')}>
        {draft.rows.map((row, index) => {
          const pillLabel = rowPillLabel(row, settings)
          return (
            <div key={row.key} className='d-flex align-items-center gap-2'>
              <span className={styles.reminderTimelineDot} />
              <div className='flex-grow-1 d-flex align-items-center gap-2 zocks-bg-color-gray-100 rounded-2 p-3'>
                <span>{copy.rowLabel}</span>
                <Form.Select
                  className='w-auto'
                  aria-label={`${copy.rowLabel} ${index + 1}`}
                  value={row.offsetMinutes}
                  onChange={(event) => updateRow(row.key, Number(event.currentTarget.value))}
                >
                  {settings.allowedOffsets.map((offset) => (
                    <option key={offset} value={offset} disabled={offset !== row.offsetMinutes && usedOffsets.includes(offset)}>
                      {formatReminderOffset(offset)}
                    </option>
                  ))}
                </Form.Select>
                <span className='flex-grow-1'>{copy.trailingCopy}</span>
                {pillLabel != null ? (
                  <Badge
                    bg='light'
                    className={classNames(
                      'rounded-pill px-2 py-1 fw-normal',
                      pillLabel === 'New' ? 'zocks-bg-color-primary-light zocks-text-primary' : 'border zocks-text-gray-900',
                    )}
                  >
                    {pillLabel}
                  </Badge>
                ) : null}
                <Tooltip title={removeDisabled ? copy.removeLastTooltip : undefined}>
                  <CloseButton aria-label={`${copy.removeLabel} ${index + 1}`} disabled={removeDisabled} onClick={() => removeRow(row.key)} />
                </Tooltip>
              </div>
            </div>
          )
        })}
      </div>
      <div className='d-flex align-items-center gap-2 ms-3'>
        <Button variant='primaryLinkButton' className='p-0 text-decoration-none' disabled={draft.rows.length >= MAX_REMINDER_ENTRIES} onClick={addRow}>
          <i className='bi bi-plus-circle me-1' />
          {copy.addLabel}
        </Button>
        <span className='paragraph-small zocks-text-gray-600'>{`${draft.rows.length} of ${MAX_REMINDER_ENTRIES} added`}</span>
      </div>
    </div>
  )
}

interface EditFooterProps {
  draft: ReminderSettingsDraft
  dirty: boolean
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
}

const EditFooter: FC<EditFooterProps> = ({ draft, dirty, isSaving, onCancel, onSave }) => (
  <div className='d-flex align-items-end justify-content-between gap-3'>
    <div className='d-flex flex-column gap-1 paragraph-small zocks-text-gray-600'>
      {dirty ? (
        <span>
          <span className='zocks-text-primary'>•</span> Unsaved changes
        </span>
      ) : null}
      {draft.rows.some((row) => row.id == null) ? <span>{NEW_ROW_CAVEAT}</span> : null}
    </div>
    <div className='d-flex gap-2'>
      <Button variant='grayButton' onClick={onCancel}>
        Cancel
      </Button>
      <Button variant='primary' disabled={!dirty || isSaving} onClick={onSave}>
        Save changes
      </Button>
    </div>
  </div>
)

export const ReminderSettingsCard: FC<ReminderSettingsCardProps> = ({
  copy,
  settings,
  draft,
  readOnly,
  editDisabled,
  editDisabledTooltip,
  isSaving,
  error,
  onEdit,
  onCancel,
  onSave,
  onDraftChange,
  onShowEmailTemplates,
}) => {
  const dirty = draft != null && settingsChanged(draft, settings)
  return (
    <div className='d-flex flex-column gap-2'>
      <h6 className='mb-0'>{copy.title}</h6>
      <SettingsCard>
        <div className='p-4 d-flex flex-column gap-3'>
          <div className='d-flex align-items-start justify-content-between gap-3'>
            <div className='d-flex flex-column'>
              <span className='bold'>{copy.heading}</span>
              <span className='paragraph-small zocks-text-gray-600'>
                {copy.description} See which email is sent in{' '}
                <Button className='d-inline p-0 align-baseline zocks-text-primary text-decoration-underline' onClick={onShowEmailTemplates}>
                  Email templates
                </Button>
                .
              </span>
            </div>
            {draft == null ? (
              <div className='d-flex align-items-center gap-1 flex-shrink-0'>
                <span className='paragraph-small zocks-text-gray-700'>{settings.enabled ? 'On' : 'Off'}</span>
                {readOnly ? (
                  <Tooltip title='This setting is managed at the account level'>
                    <i className='bi bi-info-circle' />
                  </Tooltip>
                ) : null}
              </div>
            ) : (
              <Form.Switch
                className='flex-shrink-0'
                aria-label={copy.heading}
                checked={draft.enabled}
                onChange={(event) => onDraftChange({ ...draft, enabled: event.currentTarget.checked })}
              />
            )}
          </div>
          {copy.banner != null ? (
            <div className='d-flex align-items-center gap-2 rounded-2 p-3 zocks-bg-color-primary-light paragraph-small'>
              <i className='bi bi-info-circle flex-shrink-0' />
              {copy.banner}
            </div>
          ) : null}
          {draft == null ? (
            <ReadOnlyRows copy={copy} settings={settings} />
          ) : (
            <>
              <EditableRows copy={copy} settings={settings} draft={draft} onDraftChange={onDraftChange} />
              <EditFooter draft={draft} dirty={dirty} isSaving={isSaving} onCancel={onCancel} onSave={onSave} />
            </>
          )}
          {error != null ? <div className='paragraph-small text-danger'>{error}</div> : null}
          {draft == null && !readOnly ? (
            <Tooltip title={editDisabled ? editDisabledTooltip : undefined}>
              <Button variant='primaryLinkButton' className='p-0 text-decoration-none align-self-start' disabled={editDisabled} onClick={onEdit}>
                <i className='bi bi-pencil me-1' />
                Edit
              </Button>
            </Tooltip>
          ) : null}
        </div>
      </SettingsCard>
    </div>
  )
}
